// # Library Statistics Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import { doc, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { FaChevronDown, FaChevronRight, FaExclamationTriangle, FaCheckCircle, FaClock, FaRegSmile, FaSmile, FaMeh, FaSadTear, FaCog } from 'react-icons/fa';
import './stats1_lib.css';
import { getBasePath } from '../m1_access';
import PropTypes from 'prop-types';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';

// # Import chapter topics mapping
import { chapterTopics } from '../../../general/library/step1/step1_chapter.js';
import { chapters } from '../../../general/library/step1/library_step_1.js';

// # Helper function to calculate duration between two dates
const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
};

// # Helper function to format duration
const formatDuration = (duration) => {
  if (!duration) return 'Not completed';

  const parts = [];
  if (duration.days > 0) parts.push(`${duration.days}d`);
  if (duration.hours > 0) parts.push(`${duration.hours}h`);
  if (duration.minutes > 0) parts.push(`${duration.minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '< 1m';
};

// # Calculate total topics from enabled chapters
const TOTAL_HARDCODED_TOPICS = chapters.reduce((total, chapter) => {
  if (chapter.enabled) {
    // # Special handling for chapters with naming inconsistencies
    let chapterSlug;
    if (chapter.title === 'Hematology & Oncology') {
      chapterSlug = 'hematology_and_oncology';
    } else if (chapter.title === 'Musculoskeletal & Connective Tissue') {
      chapterSlug = 'musculoskeletal_and_connective_tissue';
    } else {
      chapterSlug = chapter.title.toLowerCase().replace(/[\s&]+/g, '_');
    }
    return total + (chapterTopics[chapterSlug]?.length || 0);
  }
  return total;
}, 0);

// # Chapter icons mapping
const chapterIcons = {
  biochemistry: biochemistryIcon,
  immunology: immunologyIcon,
  microbiology: infectiousIcon,
  general_pathology: pathologyIcon,
  pharmacology: pharmaIcon,
  biostatistics: publicHealthIcon,
  cardiovascular: heartIcon,
  endocrine: endocrineIcon,
  gastrointestinal: gitIcon,
  hematology_and_oncology: bloodIcon,
  musculoskeletal_and_connective_tissue: musculoskeletalIcon,
  dermatology: skinIcon,
  neurology: cnsIcon,
  special_senses: eyeIcon,
  psychiatry: psychiatryIcon,
  renal: kidneyIcon,
  reproductive: reproductiveIcon,
  respiratory: lungsIcon,
  rapid_review: revisionIcon
};

// # Review intervals in milliseconds - now as default values
const DEFAULT_REVIEW_INTERVALS = {
  easy: 120 * 24 * 60 * 60 * 1000, // 120 days
  good: 60 * 24 * 60 * 60 * 1000,  // 60 days
  hard: 14 * 24 * 60 * 60 * 1000,  // 14 days
  oh_no: 3 * 24 * 60 * 60 * 1000   // 3 days
};

function LibraryStats({ isAdmin, isTutor, isMentorView, isDarkMode, targetUserId }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [expandedChapters, setExpandedChapters] = useState({});
  const [libraryData, setLibraryData] = useState(null);
  const [hardTopics, setHardTopics] = useState([]);
  const [ohNoTopics, setOhNoTopics] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [lowPerformers, setLowPerformers] = useState([]);
  const [needsReview, setNeedsReview] = useState([]);
  const [performanceStats, setPerformanceStats] = useState({
    totalTopics: 0,
    goodAndEasyTopics: 0,
    percentage: 0
  });
  const [activeReviewTab, setActiveReviewTab] = useState('easy');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [reviewIntervals, setReviewIntervals] = useState(DEFAULT_REVIEW_INTERVALS);
  const [tempIntervals, setTempIntervals] = useState(DEFAULT_REVIEW_INTERVALS);

  // # Load library data and review settings
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    const unsubscribe = onSnapshot(doc(db, 'my_lib_progress', userId), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step1) {
          setLibraryData(data.step1);
          processLibraryData(data.step1);
        }
      }
    });

    // # Load review settings
    const loadSettings = async () => {
      try {
        const menteeDocRef = doc(db, 's1_mentee', userId);
        const menteeDocSnap = await getDoc(menteeDocRef);
        if (menteeDocSnap.exists()) {
          const settings = menteeDocSnap.data()?.settings?.review_filter;
          if (settings) {
            setReviewIntervals(settings);
            setTempIntervals(settings);
          }
        }
      } catch (error) {
        console.error('Error loading review settings:', error);
      }
    };
    loadSettings();

    return () => unsubscribe();
  }, [user, targetUserId]);

  // # Save review settings - only allow if user has edit permissions
  const saveReviewSettings = async () => {
    // # Only allow editing if user is viewing their own data or is admin
    if (targetUserId && !isAdmin) return;
    if (!user?.uid) return;
    
    const userId = targetUserId || user.uid;
    try {
      const menteeRef = doc(db, 's1_mentee', userId);
      await updateDoc(menteeRef, {
        'settings.review_filter': tempIntervals
      });
      setReviewIntervals(tempIntervals);
      setShowSettingsModal(false);
      // Reprocess library data with new intervals
      if (libraryData) {
        processLibraryData(libraryData);
      }
    } catch (error) {
      console.error('Error saving review settings:', error);
    }
  };

  // # Process library data with current intervals
  const processLibraryData = (data) => {
    const hardList = [];
    const ohNoList = [];
    const reviewList = [];
    const topList = [];
    const lowList = [];
    let totalCompleted = 0;
    let goodAndEasy = 0;

    Object.entries(data).forEach(([chapterSlug, chapterData]) => {
      if (typeof chapterData === 'object' && chapterData !== null) {
        Object.entries(chapterData).forEach(([topicId, topicData]) => {
          if (topicId !== 'iscomplete' && topicId !== 'iscomplete_at' && topicData?.completed) {
            totalCompleted++;

            // # Format chapter name
            const chapterName = chapterSlug
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            // # Get topic name from chapterTopics
            const topicName = chapterTopics[chapterSlug]?.find(t => t.id === topicId)?.name || topicId;

            const topicInfo = {
              chapter: chapterSlug,
              chapterName,
              topicId,
              topicName,
              completedAt: topicData.completedAt,
              reviewCount: topicData.reviewTimestamps?.length || 0,
              lastReview: topicData.reviewTimestamps?.length ? 
                Math.max(...topicData.reviewTimestamps.map(ts => new Date(ts).getTime())) : 
                new Date(topicData.completedAt).getTime(),
              percentage: topicData.percentage || 0,
              difficulty: topicData.difficulty
            };

            // # Categorize by difficulty
            if (topicData.difficulty === 'hard') {
              hardList.push(topicInfo);
            } else if (topicData.difficulty === 'oh_no') {
              ohNoList.push(topicInfo);
            } else if (['good', 'easy'].includes(topicData.difficulty)) {
              goodAndEasy++;
            }

            // # Check if needs review - now using dynamic intervals
            const timeSinceLastReview = Date.now() - topicInfo.lastReview;
            if (timeSinceLastReview > reviewIntervals[topicData.difficulty]) {
              reviewList.push({
                ...topicInfo,
                daysSinceReview: Math.floor(timeSinceLastReview / (24 * 60 * 60 * 1000))
              });
            }

            // # Add to performance lists
            if (topicData.percentage) {
              if (topList.length < 5 || topicData.percentage > topList[topList.length - 1].percentage) {
                topList.push(topicInfo);
                topList.sort((a, b) => b.percentage - a.percentage);
                if (topList.length > 5) topList.pop();
              }

              if (lowList.length < 5 || topicData.percentage < lowList[lowList.length - 1].percentage) {
                lowList.push(topicInfo);
                lowList.sort((a, b) => a.percentage - b.percentage);
                if (lowList.length > 5) lowList.pop();
              }
            }
          }
        });
      }
    });

    // # Sort lists
    hardList.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
    ohNoList.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
    reviewList.sort((a, b) => b.daysSinceReview - a.daysSinceReview);

    // # Update state
    setHardTopics(hardList);
    setOhNoTopics(ohNoList);
    setNeedsReview(reviewList);
    setTopPerformers(topList);
    setLowPerformers(lowList);
    setPerformanceStats({
      totalTopics: totalCompleted,
      goodAndEasyTopics: goodAndEasy,
      percentage: totalCompleted ? Math.round((goodAndEasy / totalCompleted) * 100) : 0
    });
  };

  // # Toggle chapter expansion
  const toggleChapterExpansion = (chapterSlug) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterSlug]: !prev[chapterSlug]
    }));
  };

  // # Handle topic navigation
  const handleTopicClick = (chapter, topicId) => {
    const isAdmin = window.location.pathname.startsWith('/admin');
    let basePath;
    
    if (isAdmin) {
      basePath = '/admin/library/step1';
    } else if (isTutor) {
      basePath = '/tutor-dashboard/library/step1';
    } else {
      basePath = '/library/step1';
    }
    
    navigate(`${basePath}/${chapter}/topic/${topicId}`);
  };

  // # Group topics by chapter
  const groupByChapter = (topics) => {
    const grouped = {};
    topics.forEach(topic => {
      if (!grouped[topic.chapter]) {
        grouped[topic.chapter] = [];
      }
      grouped[topic.chapter].push(topic);
    });
    return grouped;
  };

  // # Settings Modal Component
  const SettingsModal = () => {
    // # Local state for input values - moved outside conditional
    const [inputValues, setInputValues] = useState(() => {
      return Object.entries(tempIntervals).reduce((acc, [difficulty, ms]) => ({
        ...acc,
        [difficulty]: Math.floor(ms / (24 * 60 * 60 * 1000))
      }), {});
    });

    if (!showSettingsModal) return null;

    const handleIntervalChange = (difficulty, value) => {
      setInputValues(prev => ({
        ...prev,
        [difficulty]: value
      }));
    };

    const handleSave = () => {
      // Convert days back to milliseconds and update tempIntervals
      const newIntervals = Object.entries(inputValues).reduce((acc, [difficulty, days]) => ({
        ...acc,
        [difficulty]: Math.max(1, parseInt(days || 1)) * 24 * 60 * 60 * 1000
      }), {});
      
      setTempIntervals(newIntervals);
      saveReviewSettings();
    };

    return (
      <div className="stats1_lib_modal_overlay" onClick={(e) => {
        if (e.target === e.currentTarget) setShowSettingsModal(false);
      }}>
        <div className="stats1_lib_modal">
          <h3>Review Interval Settings</h3>
          <div className="stats1_lib_modal_content">
            {Object.entries(tempIntervals).map(([difficulty]) => (
              <div key={difficulty} className="stats1_lib_interval_setting">
                <label>{difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}:</label>
                <input
                  type="number"
                  min="1"
                  value={inputValues[difficulty]}
                  onChange={(e) => handleIntervalChange(difficulty, e.target.value)}
                />
                <span>days</span>
              </div>
            ))}
          </div>
          <div className="stats1_lib_modal_actions">
            <button onClick={() => setShowSettingsModal(false)}>Cancel</button>
            <button onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`stats1_lib_container ${isDarkMode ? 'stats1_lib_dark' : ''}`}>
      {/* Performance Overview */}
      <div className="stats1_lib_section">
        <h2 className="stats1_lib_section_title">Performance Overview</h2>
        <div className="stats1_lib_performance_stats">
          <div className="stats1_lib_stat_card">
            <h3>Topics that are easy or good divided by all completed topics</h3>
            <p className="stats1_lib_stat_description">
              This is a good indicator of how well you are doing in the library.
            </p>
            <div className="stats1_lib_stat_value">
              {performanceStats.goodAndEasyTopics}/{performanceStats.totalTopics}
              <span className="stats1_lib_stat_percentage">({performanceStats.percentage}%)</span>
            </div>
            <div className="stats1_lib_progress_bar">
              <div 
                className="stats1_lib_progress_fill"
                style={{ width: `${performanceStats.percentage}%` }}
              ></div>
              <span className="stats1_lib_progress_text">
                {performanceStats.percentage}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="stats1_lib_section">
        <h2 className="stats1_lib_section_title">Progress Overview</h2>
        <div className="stats1_lib_progress_overview">
          <div className="stats1_lib_stat_card">
            <h3>Total Progress</h3>
            <div className="stats1_lib_stat_value">
              {Object.values(libraryData || {}).reduce((total, chapter) => 
                total + Object.entries(chapter).filter(([key, value]) => 
                  key !== 'iscomplete' && 
                  key !== 'iscomplete_at' && 
                  value?.completed
                ).length
              , 0)}/{TOTAL_HARDCODED_TOPICS}
              <span className="stats1_lib_stat_percentage">
                ({Math.round((Object.values(libraryData || {}).reduce((total, chapter) => 
                  total + Object.entries(chapter).filter(([key, value]) => 
                    key !== 'iscomplete' && 
                    key !== 'iscomplete_at' && 
                    value?.completed
                  ).length
                , 0) / TOTAL_HARDCODED_TOPICS) * 100)}%)
              </span>
            </div>
            <div className="stats1_lib_progress_bar">
              <div 
                className="stats1_lib_progress_fill"
                style={{ width: `${Math.round((Object.values(libraryData || {}).reduce((total, chapter) => 
                  total + Object.entries(chapter).filter(([key, value]) => 
                    key !== 'iscomplete' && 
                    key !== 'iscomplete_at' && 
                    value?.completed
                  ).length
                , 0) / TOTAL_HARDCODED_TOPICS) * 100)}%` }}
              ></div>
            </div>
            {libraryData && (
              <div className="stats1_lib_total_duration">
                <FaClock className="stats1_lib_duration_icon" />
                <span>Total Time: {formatDuration(Object.entries(libraryData).reduce((totalDuration, [chapterSlug, chapterData]) => {
                  // # Calculate duration from individual topics
                  const topicDurations = Object.entries(chapterData)
                    .filter(([key, value]) => 
                      key !== 'iscomplete' && 
                      key !== 'iscomplete_at' && 
                      key !== 'ChptStartedAt' &&
                      key !== 'IsChapStarted' &&
                      value?.TopicStartedAt
                    )
                    .map(([_, topicData]) => 
                      calculateDuration(
                        topicData.TopicStartedAt, 
                        topicData.completedAt || new Date().toISOString()
                      )
                    )
                    .filter(duration => duration !== null);

                  // # Sum up durations
                  return topicDurations.reduce((acc, duration) => ({
                    days: (acc?.days || 0) + duration.days,
                    hours: (acc?.hours || 0) + duration.hours,
                    minutes: (acc?.minutes || 0) + duration.minutes
                  }), totalDuration);
                }, null))}</span>
              </div>
            )}
          </div>

          {libraryData && (
            <div className="stats1_lib_timeline_cards">
              {/* First Started Topic */}
              {(() => {
                let firstStartedTopic = null;
                let firstStartedChapter = null;

                Object.entries(libraryData).forEach(([chapterSlug, chapterData]) => {
                  Object.entries(chapterData).forEach(([topicId, topicData]) => {
                    if (topicId !== 'iscomplete' && 
                        topicId !== 'iscomplete_at' && 
                        topicId !== 'ChptStartedAt' &&
                        topicId !== 'IsChapStarted' &&
                        topicData?.TopicStartedAt &&
                        (!firstStartedTopic || new Date(topicData.TopicStartedAt) < new Date(firstStartedTopic.startTime))) {
                      firstStartedTopic = {
                        chapterSlug,
                        topicId,
                        startTime: topicData.TopicStartedAt
                      };
                    }
                  });
                });

                if (firstStartedTopic) {
                  const topicName = chapterTopics[firstStartedTopic.chapterSlug]?.find(t => t.id === firstStartedTopic.topicId)?.name || firstStartedTopic.topicId;
                  const chapterName = firstStartedTopic.chapterSlug.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

                  return (
                    <div className="stats1_lib_timeline_card">
                      <div className="stats1_lib_timeline_icon">
                        <img src={chapterIcons[firstStartedTopic.chapterSlug]} alt={chapterName} />
                      </div>
                      <div className="stats1_lib_timeline_content">
                        <h4>First Started Topic</h4>
                        <div className="stats1_lib_timeline_topic">{topicName}</div>
                        <div className="stats1_lib_timeline_chapter">{chapterName}</div>
                        <div className="stats1_lib_timeline_time">
                          {new Date(firstStartedTopic.startTime).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Last Started Topic */}
              {(() => {
                let lastStartedTopic = null;
                let lastStartedChapter = null;

                Object.entries(libraryData).forEach(([chapterSlug, chapterData]) => {
                  Object.entries(chapterData).forEach(([topicId, topicData]) => {
                    if (topicId !== 'iscomplete' && 
                        topicId !== 'iscomplete_at' && 
                        topicId !== 'ChptStartedAt' &&
                        topicId !== 'IsChapStarted' &&
                        topicData?.TopicStartedAt &&
                        (!lastStartedTopic || new Date(topicData.TopicStartedAt) > new Date(lastStartedTopic.startTime))) {
                      lastStartedTopic = {
                        chapterSlug,
                        topicId,
                        startTime: topicData.TopicStartedAt
                      };
                    }
                  });
                });

                if (lastStartedTopic) {
                  const topicName = chapterTopics[lastStartedTopic.chapterSlug]?.find(t => t.id === lastStartedTopic.topicId)?.name || lastStartedTopic.topicId;
                  const chapterName = lastStartedTopic.chapterSlug.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

                  return (
                    <div className="stats1_lib_timeline_card">
                      <div className="stats1_lib_timeline_icon">
                        <img src={chapterIcons[lastStartedTopic.chapterSlug]} alt={chapterName} />
                      </div>
                      <div className="stats1_lib_timeline_content">
                        <h4>Last Started Topic</h4>
                        <div className="stats1_lib_timeline_topic">{topicName}</div>
                        <div className="stats1_lib_timeline_chapter">{chapterName}</div>
                        <div className="stats1_lib_timeline_time">
                          {new Date(lastStartedTopic.startTime).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
          )}

          <div className="stats1_lib_chapter_progress">
            {Object.entries(libraryData || {})
              .map(([chapterSlug, chapterData]) => {
                // # Find the chapter in the chapters array
                const chapter = chapters.find(c => {
                  if (c.title === 'Hematology & Oncology' && chapterSlug === 'hematology_and_oncology') return true;
                  if (c.title === 'Musculoskeletal & Connective Tissue' && chapterSlug === 'musculoskeletal_and_connective_tissue') return true;
                  return c.title.toLowerCase().replace(/[\s&]+/g, '_') === chapterSlug;
                });

                // # Skip if chapter is not enabled
                if (!chapter?.enabled) return null;

                const completedTopics = Object.entries(chapterData).filter(([key, value]) => 
                  key !== 'iscomplete' && 
                  key !== 'iscomplete_at' && 
                  value?.completed
                ).length;
                const totalTopics = chapterTopics[chapterSlug]?.length || 0;
                const percentage = totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0;

                // # Calculate chapter duration from topic durations
                const chapterDuration = Object.entries(chapterData)
                  .filter(([key, value]) => 
                    key !== 'iscomplete' && 
                    key !== 'iscomplete_at' && 
                    key !== 'ChptStartedAt' &&
                    key !== 'IsChapStarted' &&
                    value?.TopicStartedAt
                  )
                  .reduce((totalDuration, [_, topicData]) => {
                    const duration = calculateDuration(
                      topicData.TopicStartedAt,
                      topicData.completedAt || new Date().toISOString()
                    );
                    if (!duration) return totalDuration;
                    return {
                      days: (totalDuration?.days || 0) + duration.days,
                      hours: (totalDuration?.hours || 0) + duration.hours,
                      minutes: (totalDuration?.minutes || 0) + duration.minutes
                    };
                  }, null);

                // # Get chapter topics data
                const topicsData = chapterTopics[chapterSlug]?.map(topic => {
                  const topicData = chapterData[topic.id] || {};
                  const duration = topicData.TopicStartedAt ? 
                    calculateDuration(
                      topicData.TopicStartedAt, 
                      topicData.completedAt || new Date().toISOString()
                    ) : null;
                  
                  return {
                    ...topic,
                    isStarted: topicData.IsTopicStarted || false,
                    startedAt: topicData.TopicStartedAt,
                    isComplete: topicData.completed || false,
                    completedAt: topicData.completedAt,
                    difficulty: topicData.difficulty || 'n_a',
                    duration
                  };
                }) || [];

                return totalTopics > 0 ? {
                  chapterSlug,
                  completedTopics,
                  totalTopics,
                  percentage,
                  topicsData,
                  chapterDuration,
                  render: () => (
                    <div 
                      key={chapterSlug} 
                      className="stats1_lib_chapter_progress_item"
                      onClick={() => toggleChapterExpansion(chapterSlug)}
                    >
                      <div className="stats1_lib_chapter_header">
                        <div className="stats1_lib_chapter_info">
                          {expandedChapters[chapterSlug] ? <FaChevronDown /> : <FaChevronRight />}
                          <img 
                            src={chapterIcons[chapterSlug]} 
                            alt={chapterSlug} 
                            className="stats1_lib_chapter_icon"
                          />
                          <span>{chapterSlug.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}</span>
                        </div>
                        <div className="stats1_lib_chapter_stats">
                          <span>{completedTopics}/{totalTopics}</span>
                          <div className="stats1_lib_progress_bar">
                            <div 
                              className="stats1_lib_progress_fill"
                              style={{ width: `${percentage}%` }}
                            ></div>
                            <span className="stats1_lib_progress_text">
                              {percentage}%
                            </span>
                          </div>
                          {chapterDuration && (
                            <div className="stats1_lib_chapter_duration">
                              <FaClock className="stats1_lib_duration_icon" />
                              <span>{formatDuration(chapterDuration)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {expandedChapters[chapterSlug] && (
                        <div className="stats1_lib_chapter_topics">
                          <div className="stats1_lib_topic_grid">
                            {topicsData.map(topic => (
                              <div key={topic.id} className="stats1_lib_topic_card">
                                <div className="stats1_lib_topic_title">{topic.name}</div>
                                <div className="stats1_lib_topic_details">
                                  <div className="stats1_lib_topic_detail">
                                    <span>Status:</span>
                                    <span className={`stats1_lib_topic_status ${
                                      topic.isComplete ? 'stats1_lib_status_complete' :
                                      topic.isStarted ? 'stats1_lib_status_started' :
                                      'stats1_lib_status_not_started'
                                    }`}>
                                      {topic.isComplete ? 'Completed' :
                                       topic.isStarted ? 'Started' :
                                       'Not Started'}
                                    </span>
                                  </div>
                                  {topic.isStarted && (
                                    <div className="stats1_lib_topic_detail">
                                      <span>Started:</span>
                                      <span>{new Date(topic.startedAt).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  {topic.isComplete && (
                                    <>
                                      <div className="stats1_lib_topic_detail">
                                        <span>Completed:</span>
                                        <span>{new Date(topic.completedAt).toLocaleDateString()}</span>
                                      </div>
                                      <div className="stats1_lib_topic_detail">
                                        <span>Difficulty:</span>
                                        <span className={`stats1_lib_difficulty_tag ${topic.difficulty}`}>
                                          {topic.difficulty.charAt(0).toUpperCase() + topic.difficulty.slice(1)}
                                        </span>
                                      </div>
                                      <div className="stats1_lib_topic_detail">
                                        <span>Duration:</span>
                                        <span className="stats1_lib_duration">
                                          {formatDuration(topic.duration)}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                } : null;
              })
              .filter(Boolean) // Remove null entries
              .sort((a, b) => b.percentage - a.percentage) // Sort by percentage descending
              .map(chapter => chapter.render())} {/* Render sorted chapters */}
          </div>
        </div>
      </div>

      {/* Difficult Topics */}
      <div className="stats1_lib_section">
        <h2 className="stats1_lib_section_title">Difficult Topics</h2>
        
        {/* Hard Topics */}
        <div className="stats1_lib_difficulty_section">
          <h3>
            <FaExclamationTriangle className="stats1_lib_icon_hard" />
            Hard Topics ({hardTopics.length})
          </h3>
          {Object.entries(groupByChapter(hardTopics)).map(([chapter, topics]) => (
            <div key={chapter} className="stats1_lib_chapter_group">
              <div 
                className="stats1_lib_chapter_header"
                onClick={() => toggleChapterExpansion(`hard_${chapter}`)}
              >
                {expandedChapters[`hard_${chapter}`] ? <FaChevronDown /> : <FaChevronRight />}
                <img 
                  src={chapterIcons[chapter]} 
                  alt={chapter}
                  className="stats1_lib_chapter_icon"
                />
                <span>{chapter.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                <span className="stats1_lib_topic_count">({topics.length})</span>
              </div>
              {expandedChapters[`hard_${chapter}`] && (
                <div className="stats1_lib_topic_list">
                  {topics.map((topic, index) => (
                    <div 
                      key={index}
                      className="stats1_lib_topic_item"
                      onClick={() => handleTopicClick(topic.chapter, topic.topicId)}
                    >
                      <div className="stats1_lib_topic_name">{topic.topicName}</div>
                      <div className="stats1_lib_topic_meta">
                        <span>Reviews: {topic.reviewCount}</span>
                        <span>Completed: {new Date(topic.completedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Oh No Topics */}
        <div className="stats1_lib_difficulty_section">
          <h3>
            <FaExclamationTriangle className="stats1_lib_icon_oh_no" />
            Oh No Topics ({ohNoTopics.length})
          </h3>
          {Object.entries(groupByChapter(ohNoTopics)).map(([chapter, topics]) => (
            <div key={chapter} className="stats1_lib_chapter_group">
              <div 
                className="stats1_lib_chapter_header"
                onClick={() => toggleChapterExpansion(`oh_no_${chapter}`)}
              >
                {expandedChapters[`oh_no_${chapter}`] ? <FaChevronDown /> : <FaChevronRight />}
                <img 
                  src={chapterIcons[chapter]} 
                  alt={chapter}
                  className="stats1_lib_chapter_icon"
                />
                <span>{chapter.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                <span className="stats1_lib_topic_count">({topics.length})</span>
              </div>
              {expandedChapters[`oh_no_${chapter}`] && (
                <div className="stats1_lib_topic_list">
                  {topics.map((topic, index) => (
                    <div 
                      key={index}
                      className="stats1_lib_topic_item"
                      onClick={() => handleTopicClick(topic.chapter, topic.topicId)}
                    >
                      <div className="stats1_lib_topic_name">{topic.topicName}</div>
                      <div className="stats1_lib_topic_meta">
                        <span>Reviews: {topic.reviewCount}</span>
                        <span>Completed: {new Date(topic.completedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Topics Needing Review */}
      <div className="stats1_lib_section">
        <h2 className="stats1_lib_section_title">
          <FaClock className="stats1_lib_icon_review" />
          Topics Needing Review ({needsReview.length})
          {(!targetUserId || isAdmin) && (
            <FaCog 
              className="stats1_lib_settings_icon" 
              onClick={() => setShowSettingsModal(true)}
            />
          )}
        </h2>
        <div className="stats1_lib_review_tabs">
          <div className="stats1_lib_review_tab_buttons">
            <button 
              className={`stats1_lib_review_tab_button ${activeReviewTab === 'easy' ? 'active' : ''}`}
              onClick={() => setActiveReviewTab('easy')}
            >
              <FaRegSmile className="stats1_lib_icon_easy" />
              Easy ({needsReview.filter(t => t.difficulty === 'easy').length})
            </button>
            <button 
              className={`stats1_lib_review_tab_button ${activeReviewTab === 'good' ? 'active' : ''}`}
              onClick={() => setActiveReviewTab('good')}
            >
              <FaSmile className="stats1_lib_icon_good" />
              Good ({needsReview.filter(t => t.difficulty === 'good').length})
            </button>
            <button 
              className={`stats1_lib_review_tab_button ${activeReviewTab === 'hard' ? 'active' : ''}`}
              onClick={() => setActiveReviewTab('hard')}
            >
              <FaMeh className="stats1_lib_icon_hard" />
              Hard ({needsReview.filter(t => t.difficulty === 'hard').length})
            </button>
            <button 
              className={`stats1_lib_review_tab_button ${activeReviewTab === 'oh_no' ? 'active' : ''}`}
              onClick={() => setActiveReviewTab('oh_no')}
            >
              <FaSadTear className="stats1_lib_icon_oh_no" />
              Oh No ({needsReview.filter(t => t.difficulty === 'oh_no').length})
            </button>
          </div>
          <div className="stats1_lib_review_list">
            {needsReview
              .filter(topic => topic.difficulty === activeReviewTab)
              .map((topic, index) => (
                <div 
                  key={index}
                  className="stats1_lib_review_item"
                  onClick={() => handleTopicClick(topic.chapter, topic.topicId)}
                >
                  <div className="stats1_lib_review_header">
                    <img 
                      src={chapterIcons[topic.chapter]} 
                      alt={topic.chapterName}
                      className="stats1_lib_chapter_icon"
                    />
                    <div className="stats1_lib_review_info">
                      <div className="stats1_lib_review_chapter">{topic.chapterName}</div>
                      <div className="stats1_lib_review_topic">{topic.topicName}</div>
                    </div>
                    <div className={`stats1_lib_review_difficulty stats1_lib_difficulty_${topic.difficulty}`}>
                      {topic.difficulty}
                    </div>
                  </div>
                  <div className="stats1_lib_review_meta">
                    <span>Last Review: {new Date(topic.lastReview).toLocaleDateString()}</span>
                    <span>Days Since: {topic.daysSinceReview}</span>
                    <span>Total Reviews: {topic.reviewCount}</span>
                  </div>
                </div>
            ))}
            {needsReview.filter(topic => topic.difficulty === activeReviewTab).length === 0 && (
              <div className="stats1_lib_no_reviews">
                No topics of this difficulty level need review
              </div>
            )}
          </div>
        </div>
        {showSettingsModal && <SettingsModal />}
      </div>
    </div>
  );
}

// # Update PropTypes
LibraryStats.propTypes = {
  isDarkMode: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isTutor: PropTypes.bool,
  isMentorView: PropTypes.bool,
  targetUserId: PropTypes.string
};

export default LibraryStats; 